import { useTranslation } from "react-i18next";

import { useQueryClient } from "@tanstack/react-query";

import { useAlertContext } from "../../../alert/AlertContext";
import { PendingDocumentMove } from "../../api/ClientFileApi";
import { useOptionalDocumentDndApi } from "../../api/ClientFileApiContext";

/**
 * Persists a view's pending document moves on save. Every move is executed independently, failures
 * are reported per file and returned so the caller keeps them pending in its form, and every
 * document list a cross-zone move can affect is refreshed. Without the documentDnd slice no moves
 * can ever be queued (the guard is defensive only).
 */
export function useAssetDocumentMoves(clientId: string) {
  const { t } = useTranslation();
  const { showError } = useAlertContext();
  const queryClient = useQueryClient();
  const dndApi = useOptionalDocumentDndApi();

  return async (moves: PendingDocumentMove[]): Promise<PendingDocumentMove[]> => {
    if (moves.length === 0 || !dndApi) {
      return [];
    }

    const failed: PendingDocumentMove[] = [];
    await Promise.all(
      moves.map(async (move) => {
        try {
          await dndApi.moveDocument({ clientId, target: move.target, documentId: move.document.documentId });
        } catch {
          failed.push(move);
          showError(t("dropzone.dnd.moveFileError", { fileName: move.document.fileName }));
        }
      })
    );

    dndApi.invalidateDocumentZoneQueries(queryClient, clientId);
    return failed;
  };
}
